import { useMutation } from '@apollo/client';
import React from 'react';
import * as styles from './CreateVerificationModal.module.scss';
import type { VerificationFormValues } from './VerificationFrequencyFields';
import VerificationFrequencyFields, {
  FREQUENCIES,
  validateVerificationFrequencyFields,
} from './VerificationFrequencyFields';
import {
  VerificationModalHr,
  VerificationModalNotice,
} from './VerificationModal';
import VerificationVerifiersFields from './VerificationVerifiersFields';
import VerifiedIcon from './VerifiedIcon';
import VerifiedToast from './VerifiedToast';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Form, { Field, Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';

const CREATE_VERIFICATION_MUTATION = gql(/** @complexity 20 */ `
  mutation createVerification(
    $postId: ID!
    $unit: VerificationUnit!
    $quantity: Int!
    $primaryVerifierId: ID!
    $secondaryVerifierIds: [ID!]!
  ) {
    verification: createVerification(
      postId: $postId
      unit: $unit
      quantity: $quantity
      primaryVerifierId: $primaryVerifierId
      secondaryVerifierIds: $secondaryVerifierIds
    ) {
      id
      post {
        id
        verifiedAt
        verification {
          id
          unit
          quantity
          insertedAt
          primaryVerifier {
            id
          }
          secondaryVerifiers {
            id
          }
        }
        verificationEntries {
          id
          verifiedAt
          version
          expiredAt
          verifier {
            id
          }
        }
      }
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment CreateVerificationModal_post on Post {
    id
    owner {
      id
    }
    ...VerificationVerifiersFields_post
  }
`);

interface CreateVerificationModalProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
}

type FormValues = VerificationFormValues & {
  primaryVerifierId: string;
  secondaryVerifierIds: string[];
  confirm: boolean;
};

const CreateVerificationModal = ({
  post,
  close,
}: CreateVerificationModalProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const [createVerification] = useMutation(CREATE_VERIFICATION_MUTATION);

  const onSubmit = async ({
    unit,
    quantity: quantityString,
    primaryVerifierId,
    secondaryVerifierIds,
  }: FormValues) => {
    if (!unit) {
      return null;
    }
    const quantity = Number(quantityString);
    return createVerification({
      variables: {
        unit,
        quantity,
        postId: post.id,
        primaryVerifierId,
        secondaryVerifierIds,
      },
    }).then(() => {
      close();
      if (
        primaryVerifierId === currentUser?.id ||
        secondaryVerifierIds.includes(currentUser?.id)
      ) {
        dispatch(
          UIActions.pushToast((toastProps) => (
            <VerifiedToast
              onDismiss={toastProps.onDismiss}
              unit={unit}
              quantity={quantity}
            />
          )),
        );
      }
    });
  };

  return (
    <Modal width={534}>
      <ModalTitle
        subtitle="Verifiers must periodically review verified posts so that the post retains verified status."
        close={close}
      >
        Verify post <VerifiedIcon inline size="large" />
      </ModalTitle>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          ...FREQUENCIES[0],
          primaryVerifierId: post.owner.id,
          secondaryVerifierIds: [],
          confirm: false,
        }}
        validate={validateVerificationFrequencyFields}
      >
        {({ values, setFieldValue, errors, isValid, dirty }) => {
          const isCurrentUserVerifier =
            values.primaryVerifierId === currentUser?.id ||
            values.secondaryVerifierIds.includes(currentUser?.id);

          const isDisabled = (isCurrentUserVerifier && !dirty) || !isValid;

          return (
            <>
              <VerificationVerifiersFields
                post={post}
                primaryVerifierId={values.primaryVerifierId}
                secondaryVerifierIds={values.secondaryVerifierIds}
                setPrimaryVerifier={(id) =>
                  setFieldValue('primaryVerifierId', id)
                }
                setSecondaryVerifierIds={(ids) =>
                  setFieldValue('secondaryVerifierIds', ids)
                }
              />
              <VerificationModalHr />
              <VerificationFrequencyFields
                frequency={values.frequency}
                setFieldValue={setFieldValue}
              />

              {(Object.keys(errors).length === 0 ||
                (Object.keys(errors).length === 1 &&
                  Object.keys(errors).includes('confirm'))) &&
                values.unit &&
                values.quantity != null && (
                  <VerificationModalNotice
                    unit={values.unit}
                    quantity={values.quantity}
                  >
                    {(dateString) =>
                      `We’ll notify you to reverify this post on ${dateString}.`
                    }
                  </VerificationModalNotice>
                )}
              {isCurrentUserVerifier ? (
                <>
                  <VerificationModalHr />
                  <label>
                    <div className={styles.label}>Confirm</div>
                    <Field
                      name="confirm"
                      label="I’ve reviewed this post for accuracy"
                      type="checkbox"
                      required
                    />
                  </label>
                </>
              ) : null}
              <ModalActionFooter
                primary={
                  <Submit size="large" disabled={isDisabled}>
                    {isCurrentUserVerifier ? 'Verify' : 'Request verification'}
                  </Submit>
                }
              />
            </>
          );
        }}
      </Form>
    </Modal>
  );
};

export default CreateVerificationModal;
